import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { refreshSession } from '@/lib/api';
import { Mark } from '@/components/AppShell';

/**
 * The redirect target after Google.
 *
 * The callback itself is a browser redirect handled by the server; by the time
 * we land here the refresh cookie is already set. All that remains is to
 * exchange it for an access token, which is one call to `POST /auth/refresh`.
 *
 * A failed exchange goes back to the sign-in page carrying the reason, so the
 * person gets a specific sentence rather than a spinner that eventually gives
 * up on them.
 */
export function AuthCallbackPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const ran = useRef(false);
  const [slow, setSlow] = useState(false);

  useEffect(() => {
    // React 18+ mounts effects twice in development; a second exchange would
    // consume the rotated refresh token and revoke the family.
    if (ran.current) return;
    ran.current = true;

    const failed = params.get('auth') === 'failed';
    const reason = params.get('reason');

    if (failed) {
      navigate(`/login?auth=failed&reason=${encodeURIComponent(reason ?? 'exchange_failed')}`, { replace: true });
      return;
    }

    const slowTimer = setTimeout(() => setSlow(true), 4000);

    void (async () => {
      const token = await refreshSession();
      clearTimeout(slowTimer);
      navigate(token ? '/' : '/login?auth=failed&reason=exchange_failed', { replace: true });
    })();

    return () => clearTimeout(slowTimer);
  }, [params, navigate]);

  return (
    <div className="chassis flex items-center justify-center px-[30px]">
      <div className="text-center">
        <span className="inline-block text-graphite-900">
          <Mark className="h-8 w-8 animate-ledger-pulse" />
        </span>
        <p className="mt-4 text-graphite-500">Signing you in…</p>
        {slow ? <p className="mt-1 text-sm text-graphite-500">This is taking longer than usual. Hold on.</p> : null}
      </div>
    </div>
  );
}
